import lottie from 'lottie-web';
const ITEM_SELECTOR = '.js--lottie';
const lottieMap = new Map();
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const findLastAnimatedFrame = (animationData, fallback) => {
    let last = 0;
    const walk = (node) => {
        if (!node || typeof node !== 'object')
            return;
        if (Array.isArray(node)) {
            node.forEach(walk);
            return;
        }
        const obj = node;
        if (obj.a === 1 && Array.isArray(obj.k)) {
            obj.k.forEach((keyframe) => {
                if (typeof keyframe.t === 'number' && keyframe.t > last) {
                    last = keyframe.t;
                }
            });
        }
        Object.values(obj).forEach(walk);
    };
    walk(animationData);
    return last > 0 ? last : fallback;
};
const initSequentialLottie = (sectionSelector, options = {}) => {
    const { itemSelector = ITEM_SELECTOR, viewportThreshold = 0.5, viewportSelector } = options;
    document.querySelectorAll(sectionSelector).forEach((section) => {
        const items = Array.from(section.querySelectorAll(itemSelector));
        items.forEach((el) => {
            lottieMap.get(el)?.destroy();
            lottieMap.delete(el);
        });
        const animate = !reducedMotion();
        const sequential = animate && items.length > 1;
        const anims = [];
        const endFrames = [];
        items.forEach((el, index) => {
            const path = el.dataset.lottie;
            if (!path)
                return;
            const isActive = !sequential || index === 0;
            const anim = lottie.loadAnimation({
                container: el,
                renderer: 'svg',
                loop: false,
                autoplay: false,
                path,
            });
            anim.addEventListener('DOMLoaded', () => {
                const { animationData } = anim;
                endFrames[index] = findLastAnimatedFrame(animationData, anim.totalFrames - 1);
                if (!animate || !isActive) {
                    anim.goToAndStop(endFrames[index], true);
                }
            });
            lottieMap.set(el, anim);
            anims.push(anim);
        });
        if (!animate || !anims.length)
            return;
        let activeIndex = 0;
        let inView = false;
        let started = false;
        const handedOff = [];
        const HANDOFF_AT = 0.78;
        const playActiveFromStart = () => {
            const anim = anims[activeIndex];
            const endFrame = endFrames[activeIndex] ?? anim.totalFrames - 1;
            handedOff[activeIndex] = false;
            anim.playSegments([0, endFrame], true);
        };
        const advance = (index) => {
            if (index !== activeIndex || handedOff[index])
                return;
            handedOff[index] = true;
            activeIndex = sequential ? (index + 1) % anims.length : index;
            if (inView)
                playActiveFromStart();
        };
        anims.forEach((anim, index) => {
            anim.addEventListener('enterFrame', () => {
                const endFrame = endFrames[index] ?? anim.totalFrames - 1;
                if (anim.currentFrame >= endFrame * HANDOFF_AT)
                    advance(index);
            });
            anim.addEventListener('complete', () => advance(index));
        });
        const viewportTarget = (viewportSelector && section.querySelector(viewportSelector)) || section;
        const startObserving = () => {
            new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    inView = entry.isIntersecting;
                    const current = anims[activeIndex];
                    if (!current)
                        return;
                    if (inView) {
                        if (!started) {
                            started = true;
                            playActiveFromStart();
                        }
                        else {
                            current.play();
                        }
                    }
                    else {
                        current.pause();
                    }
                });
            }, { threshold: viewportThreshold }).observe(viewportTarget);
        };
        Promise.all([
            document.fonts ? document.fonts.ready : Promise.resolve(),
            document.readyState === 'complete'
                ? Promise.resolve()
                : new Promise((resolve) => {
                    window.addEventListener('load', () => resolve(), { once: true });
                }),
        ]).then(startObserving);
    });
};
export default initSequentialLottie;
